import { AxiosError, AxiosInstance } from 'axios';
import config from 'config/Config';
import { initAxios } from 'utils/axios';
import { TaskBundle } from 'utils/customTypes';
import {
  BulkCreateOrUpdateResult,
  BulkCreateTaskTemplateDependenciesParams,
  CreateTaskTemplateDependenciesResponse,
  BulkCreateTaskTemplateDependenciesResponse,
  TaskTemplateForCreateOrUpdate,
  TaskTemplateDependency,
  FetchTaskTemplateDependenciesParams,
  FetchTaskTemplateDependenciesResponse,
  BulkDeleteTaskTemplateDependenciesParams,
  TaskTemplateDependencyFailure,
  TaskTemplateDependencyLink,
} from 'utils/types/taskTemplate';
import {
  generateTaskTemplateDependencyEdges,
  mapFailedDependencyLinks,
} from './helpers';

class ActiveTaskBundleAPI {
  instance: AxiosInstance;
  instanceV3: AxiosInstance;
  constructor() {
    this.instance = initAxios(config.get('backendURL'));
    this.instanceV3 = initAxios(config.get('backendURL'), 'dt');
  }

  fetchTaskBundle = async (bundleId: string) => {
    const { data } = await this.instance.get(`/taskBundles/${bundleId}`);
    return data;
  };

  fetchTaskBundleV3 = async (bundleId: string) => {
    const { data } = await this.instanceV3.get(`/v3/task-bundles/${bundleId}`);
    return data;
  };

  updateTaskBundle = async (updatedBundle: TaskBundle) => {
    const { data } = await this.instance.put(
      `/taskBundles/${updatedBundle.id}`,
      {
        ...updatedBundle,
      }
    );
    return data;
  };

  updateTaskBundleV3 = async (updatedBundle: TaskBundle) => {
    const { data } = await this.instanceV3.patch(
      `/v3/task-bundles/${updatedBundle.id}`,
      {
        name: updatedBundle.name,
        description: updatedBundle.description ?? null,
      }
    );
    return data;
  };

  bulkCreateOrUpdateTemplatesForBundle = async (
    templatesToCreateOrUpdate: TaskTemplateForCreateOrUpdate[],
    bundleId: string,
    creatorIsNotified?: boolean
  ) => {
    const { data } = await this.instance.post(
      `/taskTemplates/${bundleId}/bulkCreateOrUpdate`,
      {
        templatesToCreateOrUpdate,
        creatorIsNotified,
      }
    );
    return data;
  };

  bulkCreateOrUpdateTemplatesForBundleV3 = async (
    templatesToCreateOrUpdate: TaskTemplateForCreateOrUpdate[],
    bundleId: string
  ): Promise<BulkCreateOrUpdateResult[]> => {
    // Single atomic call into the header-gated bulk-reorder endpoint added
    // in CP-10792. Server replaces the bundle's template set in one
    // transaction; positions come from array order. Closes the trigger-shift
    // races and silent-corruption shape that motivated the for…of loop and
    // descending-sort shims (CP-10747 / CP-10796).
    const sortedByIndex = [...templatesToCreateOrUpdate].sort(
      (a, b) => a.index - b.index
    );

    const body = {
      templates: sortedByIndex.map((template) => ({
        id: template.action_type === 'update' ? template.id ?? null : null,
        name: template.name,
        description: template.description ?? null,
        estimatedHours:
          template.estimated_hours != null
            ? Number(template.estimated_hours)
            : null,
        type: template.type || null,
        startDay:
          template.start_day != null && template.start_day !== ''
            ? Number(template.start_day) + 1
            : null,
        duration:
          template.duration != null && template.duration !== ''
            ? Number(template.duration)
            : null,
      })),
    };

    const { data } = await this.instanceV3.post(
      `/v3/task-bundles/${bundleId}/task-templates`,
      body,
      { headers: { 'X-Cognota-Bulk-Reorder': '1' } }
    );

    const responseList: Array<{ id: string }> = data.data ?? [];

    return sortedByIndex.map((template, i) => ({
      clientId: (template.id ?? responseList[i]?.id) as string,
      serverId: responseList[i].id,
      actionType: template.action_type === 'update' ? 'update' : 'create',
    }));
  };

  bulkCreateTaskTemplateDependenciesV3 = async (
    params: BulkCreateTaskTemplateDependenciesParams
  ): Promise<BulkCreateTaskTemplateDependenciesResponse> => {
    const { bundleId, links } = params;

    const perLinkResponses = await Promise.all(
      links.map(
        async (link): Promise<BulkCreateTaskTemplateDependenciesResponse> => {
          try {
            const { data } =
              await this.instanceV3.post<CreateTaskTemplateDependenciesResponse>(
                `/v3/task-bundles/${bundleId}/task-templates/${link.taskTemplateId}/${link.direction}`,
                { dependencies: link.dependencies }
              );
            return {
              successes: generateTaskTemplateDependencyEdges(
                bundleId,
                link.taskTemplateId,
                link.direction,
                data.successes
              ),
              failedLinks: mapFailedDependencyLinks(
                bundleId,
                link.taskTemplateId,
                link.direction,
                data.failures
              ),
            };
          } catch (error) {
            const axiosErr =
              error as AxiosError<CreateTaskTemplateDependenciesResponse>;
            const serverFailures = axiosErr.response?.data?.failures;
            return {
              successes: [],
              failedLinks: mapFailedDependencyLinks(
                bundleId,
                link.taskTemplateId,
                link.direction,
                serverFailures ??
                  link.dependencies.map((id) => ({
                    id,
                    error: 'request_failed',
                  }))
              ),
            };
          }
        }
      )
    );

    return {
      successes: perLinkResponses.flatMap((response) => response.successes),
      failedLinks: perLinkResponses.flatMap((response) => response.failedLinks),
    };
  };

  fetchTaskTemplateDependencies = async (
    params: FetchTaskTemplateDependenciesParams
  ): Promise<TaskTemplateDependency[]> => {
    const { data } =
      await this.instanceV3.get<FetchTaskTemplateDependenciesResponse>(
        `/v3/task-bundles/${params.bundleId}/task-templates/${params.templateId}/${params.direction}`
      );
    return data.dependencies;
  };

  fetchTaskTemplatesForBundle = async (bundleId: string) => {
    const { data } = await this.instance.get(`/taskTemplates/${bundleId}/all`);
    return data;
  };

  fetchTaskTemplatesForBundleV3 = async (
    bundleId: string,
    includeDependencies?: boolean
  ) => {
    const headers = includeDependencies ? { 'include-dependencies': '1' } : {};
    const { data } = await this.instanceV3.get(
      `/v3/task-bundles/${bundleId}/task-templates`,
      { headers }
    );
    return data;
  };

  bulkDeleteTaskTemplatesForBundle = async (
    templatesToDelete: string[],
    bundleId: string
  ) => {
    const { data } = await this.instance.post(
      `/taskTemplates/${bundleId}/bulkDelete`,
      {
        templatesToDelete,
      }
    );
    return data;
  };

  bulkDeleteTaskTemplatesForBundleV3 = async (
    templatesToDelete: string[],
    bundleId: string
  ) => {
    await this.instanceV3.delete(
      `/v3/task-bundles/${bundleId}/task-templates`,
      { data: { ids: templatesToDelete } }
    );
  };

  bulkDeleteTaskTemplateDependencies = async (
    params: BulkDeleteTaskTemplateDependenciesParams
  ): Promise<{
    removedLinks: TaskTemplateDependencyLink[];
    failedDeps: string[];
  }> => {
    const { bundleId, links } = params;
    const removedLinks: TaskTemplateDependencyLink[] = [];
    const failedDeps = new Set<string>();

    for (const link of links) {
      try {
        const { data } = await this.instanceV3.delete<{
          failures: TaskTemplateDependencyFailure[];
        }>(
          `/v3/task-bundles/${bundleId}/task-templates/${link.taskTemplateId}/${link.direction}`,
          { data: { dependencies: link.dependencies } }
        );
        const failedIds = (data?.failures ?? []).map((failure) => failure.id);
        const successfulDeps = link.dependencies.filter(
          (dependency) => !failedIds.includes(dependency)
        );
        failedIds.forEach((id) => failedDeps.add(id));

        if (successfulDeps.length > 0) {
          removedLinks.push({
            taskTemplateId: link.taskTemplateId,
            direction: link.direction,
            dependencies: successfulDeps,
          });
        }
      } catch (error) {
        const axiosErr = error as AxiosError<{
          failures: TaskTemplateDependencyFailure[];
        }>;
        const serverFailures = axiosErr.response?.data?.failures;
        const failureIds = serverFailures
          ? serverFailures.map((f) => f.id)
          : link.dependencies;

        failureIds.forEach((id) => failedDeps.add(id));
      }
    }
    return { removedLinks, failedDeps: Array.from(failedDeps) };
  };
}

const activeTaskBundleAPI = new ActiveTaskBundleAPI();

export default activeTaskBundleAPI;
